import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
// Hooks
import { useGetMediaPost } from 'hooks/useGetMediaPost';
// Hocks
import withApollo from 'hocs/withApollo';
// Api
import { GET_MEDIA_POST, GET_MEDIA_POSTS } from 'api/mediaPost/queries';
import {
  GetMediaPost,
  GetMediaPost_getMediaPost,
  GetMediaPostVariables,
} from 'api/mediaPost/types/GetMediaPost';
import {
  GetMediaPosts,
  GetMediaPosts_getMediaPosts_entities,
  GetMediaPostsVariables,
} from 'api/mediaPost/types/GetMediaPosts';
import { StoreStatus } from 'api/graphql-global-types';
// Helpers
import { getCanonicalUrl } from 'helpers/canonical';
import {
  ApolloPageContext,
  PageProps,
  getCleanQueryParam,
  markPageAsNotFound,
} from 'helpers/next-page';
import { withPreservedQuery } from 'helpers/mediaStartTime';
import {
  cleanDescription,
  generateSocialMediaPostingSchema,
} from 'helpers/structured-data';
// Layouts
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// UI
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';
// Components
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
import MediaFeedItem from 'components/Athlete/components/MediaFeedItem/MediaFeedItem';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';
import RouteRedirect from 'components/RouteRedirect/RouteRedirect';
import { usePersonMentionPageVisitTracking } from 'hooks/usePersonMentionPageVisitTracking';
// Styles
import styles from './mediaPost.module.scss';

type MediaPostPageProps = PageProps & {
  initialMediaFeed?: GetMediaPost_getMediaPost | null;
  initialRelatedPosts?: GetMediaPosts_getMediaPosts_entities[] | null;
};

const MediaPostPageContent = ({
  initialMediaFeed,
  initialRelatedPosts,
}: {
  initialMediaFeed?: GetMediaPost_getMediaPost | null;
  initialRelatedPosts?: GetMediaPosts_getMediaPosts_entities[] | null;
}): JSX.Element => {
  usePersonMentionPageVisitTracking();
  const { asPath } = useRouter();
  const { data } = useGetMediaPost();
  const mediaFeed = data?.getMediaPost || initialMediaFeed;

  const isVideo = mediaFeed && 'video' in mediaFeed;

  if (!mediaFeed || isVideo) {
    return <FourDotsLoader />;
  }

  const image = mediaFeed.image?.url || undefined;
  const metaDescription =
    cleanDescription(mediaFeed?.body) || mediaFeed?.title || 'Media post';
  const canonicalUrl = getCanonicalUrl(asPath);
  const structuredData = generateSocialMediaPostingSchema(mediaFeed);

  return (
    <>
      <Head
        title={mediaFeed?.title || 'title'}
        description={metaDescription}
        image={image}
        canonicalLink={canonicalUrl}
        structuredData={structuredData}
      />
      <AppBannerAndModal
        when={!!mediaFeed}
        variant="profile"
        username={mediaFeed?.store?.slug ?? undefined}
        profileImageUrl={mediaFeed?.store?.storeDetails?.avatarURL ?? undefined}
      />
      <Header />

      <Main withoutSidesPadding>
        <div className={styles.root}>
          <MediaFeedItem
            mediaFeed={mediaFeed}
            initialRelatedPosts={initialRelatedPosts}
          />
        </div>
      </Main>
      <Footer />
    </>
  );
};

const MediaPostPage: NextPage<MediaPostPageProps> = ({
  isNotFound,
  redirectTo,
  initialMediaFeed,
  initialRelatedPosts,
}) => {
  if (redirectTo) {
    return <RouteRedirect to={redirectTo} />;
  }

  if (isNotFound) {
    return <NotFoundPage />;
  }

  return (
    <MediaPostPageContent
      initialMediaFeed={initialMediaFeed}
      initialRelatedPosts={initialRelatedPosts}
    />
  );
};

MediaPostPage.getInitialProps = async (
  ctx: ApolloPageContext
): Promise<MediaPostPageProps> => {
  const athleteSlug = getCleanQueryParam(ctx.query.athleteSlug);
  const mediaPostSlug = getCleanQueryParam(ctx.query.mediaPostSlug);

  if (!ctx.apolloClient || !athleteSlug || !mediaPostSlug) {
    return {};
  }

  try {
    // Parallel SSR fetch — see /video page for rationale.
    const postPromise = ctx.apolloClient.query<
      GetMediaPost,
      GetMediaPostVariables
    >({
      query: GET_MEDIA_POST,
      variables: {
        input: {
          slug: mediaPostSlug,
          storeSlug: athleteSlug,
        },
      },
    });

    const relatedPromise = ctx.apolloClient
      .query<GetMediaPosts, GetMediaPostsVariables>({
        query: GET_MEDIA_POSTS,
        variables: {
          input: {
            storeSlug: athleteSlug,
            storeStatuses: [StoreStatus.Active, StoreStatus.Pending],
          },
        },
      })
      .catch((error) => {
        Sentry.captureException(error, {
          tags: {
            section: 'ssr-prefetch',
            page: 'social-media-post/[mediaPostSlug]',
            query: 'GET_MEDIA_POSTS',
          },
        });
        return null;
      });

    const [postResult, relatedResult] = await Promise.all([
      postPromise,
      relatedPromise,
    ]);

    const mediaFeed = postResult.data?.getMediaPost;
    const initialRelatedPosts =
      relatedResult?.data?.getMediaPosts?.entities ?? null;

    if (!mediaFeed) {
      markPageAsNotFound(ctx);
      return { isNotFound: true };
    }

    if ('video' in mediaFeed && mediaFeed.store?.slug && mediaFeed.slug) {
      const redirectTo = withPreservedQuery(
        `/${mediaFeed.store.slug}/social-media-post/video/${mediaFeed.slug}`,
        ctx.query
      );

      if (ctx.res) {
        ctx.res.writeHead(301, { Location: redirectTo });
        ctx.res.end();
        return { redirectTo };
      }

      return { redirectTo };
    }

    return { initialMediaFeed: mediaFeed, initialRelatedPosts };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { section: 'ssr', page: 'social-media-post/[mediaPostSlug]' },
    });
    markPageAsNotFound(ctx);
    return { isNotFound: true };
  }

  return {};
};

// See sibling /video page for rationale on the SSR/prefetch strategy.
export default withApollo(MediaPostPage, { getDataFromTree });
